import { Section } from '@/components/layout';
import { Sparkle } from '@/components/ornaments';
import { Reveal } from '@/components/motion';
import { ProgramItem } from '@/components/ui';
import type { OrnamentIconName } from '@/components/ornaments';
import {
  formatDayLabel,
  formatLongDate,
  formatWeekdayUpper,
  isSingleBlockDay,
  type TourEvent,
} from '@/features/events';
import { BA_PROGRAM } from '../ba.content';
import styles from './BaProgram.module.css';

interface Props {
  event: TourEvent;
}

/**
 * Programa de 2 dias.
 *
 * EL DATO MANDA SOBRE EL PNG (ADR-003 §2 · desviacion B-5). El mockup dibuja
 * cuatro bloques por dia porque le cuadraba la simetria; el programa real de
 * Buenos Aires, cruzado contra `docs/brief/02_BRIEFING_ESTRATEGIA.md` §1, es:
 *
 *   sabado 23 -> 3 bloques (10:00-12:00 · 12:30-14:30 · 16:00-19:00)
 *   domingo 24 -> 1 bloque unico (10:30-13:30)
 *
 * Se conserva la COMPOSICION del mockup (dos columnas, chip de dia, aro con
 * glifo, titulo y subtitulo) y se cambia el contenido por el verdadero. Anadir
 * un cuarto bloque para que las columnas midan igual seria inventar un dato.
 */

/** Glifos por posicion, en la familia visual del mockup. */
const ICONOS: readonly OrnamentIconName[] = ['star', 'eye', 'spiral', 'seed'];

export function BaProgram({ event }: Props) {
  return (
    <Section background="alt" labelledBy="ba-programa-title" width="default" className={styles.band}>
      {/* FIDELIDAD: el mockup NO pone filete bajo este titulo — el rotulo dorado
          va solo (medido: titulo en y 783..798 y el primer chip ya en y 823, sin
          nada de oro entre medias). El `GoldLine` que habia aqui era un ornamento
          de mas y ademas separaba el titulo de las columnas. */}
      <Reveal>
        <div className={styles.heading}>
          <h2 className={styles.title} id="ba-programa-title">
            {BA_PROGRAM.title}
          </h2>
        </div>
      </Reveal>

      <ol className={styles.days}>
        <span className={styles.axis} aria-hidden="true">
          <Sparkle size={70} variant="line" />
        </span>

        {event.program.map((day, index) => {
          const unico = isSingleBlockDay(day);
          return (
            <Reveal as="li" className={styles.day} delay={index * 0.08} key={day.day}>
              <div className={styles.dayHead}>
                {/* <h3> y no <p>: `ProgramItem` pinta cada bloque como <h4>, y
                    sin este nivel intermedio la jerarquia salta de h2 a h4. */}
                <h3 className={styles.chip}>{formatDayLabel(day)}</h3>
                <p className={styles.dayDate}>
                  <time dateTime={day.date}>
                    {formatWeekdayUpper(day.date)} · {formatLongDate(day.date)}
                  </time>
                </p>
              </div>

              <p className={styles.dayTitle}>{day.title}</p>

              {unico ? <p className={styles.singleTag}>{BA_PROGRAM.singleBlockLabel}</p> : null}

              <ul className={styles.blocks} role="list">
                {/* LAS HORAS SE PUBLICAN. Esto era `showTime={false}` por
                    fidelidad al poster —que no las rotula— y era la desviacion
                    B-16: la hora existia en el dato, era correcta, y el
                    visitante de Buenos Aires no la veia en ninguna parte de la
                    seccion de programa. Las otras cuatro ciudades si la pintan;
                    esta era la unica que no.

                    El cliente lo pidio por su nombre en el video del 28-jul-2026
                    mirando ESTA seccion: «el programa meter y explicar mejor,
                    que Luis lo ha pasado en el PDF, el programa de cada dia,
                    para que tenga mucho detalle». En el contenido manda el brief
                    (ADR-006), asi que el poster se queda viejo aqui. B-16 queda
                    revocada en `VISUAL_DEVIATIONS.md`.

                    Y no es un adorno: son 10:00-12:00, 12:30-14:30, 16:00-19:00
                    el sabado y 10:30-13:30 el domingo. Quien compra un vuelo a
                    Buenos Aires necesita saber a que hora empieza y acaba cada
                    jornada ANTES de reservar el hotel, no despues. */}
                {day.blocks.map((block, blockIndex) => (
                  <ProgramItem
                    block={block}
                    icon={ICONOS[(index * 2 + blockIndex) % ICONOS.length]}
                    key={block.id}
                  />
                ))}
              </ul>
            </Reveal>
          );
        })}
      </ol>
    </Section>
  );
}
